import { cx } from '@emotion/css';
import type { CSSProperties, FC } from 'react';

import type { BaseComponentProps, ImageSources, ValueOf } from '../../types';
import { Picture } from '../Picture';
import {
  defaultStickerInsetPx,
  stickerBottomEndCss,
  stickerBottomStartCss,
  stickerCss,
  stickerImgCss,
  stickerLargeCss,
  stickerMediumCss,
  stickerRotationNegative15DegreesCss,
  stickerRotationPositive15DegreesCss,
  stickerTopEndCss,
  stickerTopStartCss,
} from './styles';

export const StickerPosition = {
  TopStart: 'Top-Start',
  TopEnd: 'Top-End',
  BottomStart: 'Bottom-Start',
  BottomEnd: 'Bottom-End',
} as const;

export const StickerRotation = {
  Negative15Degrees: '-15 degrees',
  ZeroDegrees: '0 degrees',
  Positive15Degrees: '15 degrees',
} as const;

export const StickerSize = {
  Regular: 'regular',
  Medium: 'medium',
  Large: 'large',
} as const;

export type StickerPosition = ValueOf<typeof StickerPosition>;
export type StickerRotation = ValueOf<typeof StickerRotation>;
export type StickerSize = ValueOf<typeof StickerSize>;

export interface StickerProps extends BaseComponentProps {
  imgSrcs: ImageSources;
  position?: StickerPosition;
  rotation?: StickerRotation;
  size?: StickerSize;
  insetPx?: number;
}

export const Sticker: FC<StickerProps> = ({
  className,
  imgSrcs,
  position = StickerPosition.TopStart,
  rotation = StickerRotation.ZeroDegrees,
  size = StickerSize.Regular,
  insetPx = defaultStickerInsetPx,
}) => {
  return (
    <Picture
      className={cx(
        stickerCss,
        {
          [stickerMediumCss]: size === StickerSize.Medium,
          [stickerLargeCss]: size === StickerSize.Large,
          [stickerTopStartCss]: position === StickerPosition.TopStart,
          [stickerTopEndCss]: position === StickerPosition.TopEnd,
          [stickerBottomStartCss]: position === StickerPosition.BottomStart,
          [stickerBottomEndCss]: position === StickerPosition.BottomEnd,
          [stickerRotationNegative15DegreesCss]: rotation === StickerRotation.Negative15Degrees,
          [stickerRotationPositive15DegreesCss]: rotation === StickerRotation.Positive15Degrees,
        },
        className
      )}
      style={{ '--sticker-position-offset': `${insetPx}px` } as CSSProperties}
      imgClassName={stickerImgCss}
      imgSrcs={imgSrcs}
      altText="" // Presentational only
    />
  );
};
